package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.components.widgets.VideoFormat
import com.iregained.site.components.widgets.YouTubeVideoCard
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Scale-Down Focus
@Composable
private fun Modifier.scaleDownFade(isVisible: Boolean, delayMs: Int = 0): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 1.05f).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ProductDemonstrationSection(data: ProductDemoSectionData = defaultDemoData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-demonstration-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.videoUrl.isNotBlank()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1000.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).scaleDownFade(isVisible),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.videoUrl.isNotBlank()) {
                        YouTubeVideoCard(
                            embedUrl = data.videoUrl,
                            thumbnailUrl = data.videoThumbnailUrl,
                            ariaLabel = data.videoAriaLabel,
                            labelPillText = data.videoLabelPillText,
                            videoFormat = data.videoFormat,
                            modifier = Modifier.fillMaxWidth().boxShadow(
                                0.px, 20.px, 40.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.15f)
                            ).scaleDownFade(isVisible, 200)
                        )
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS VIDEO UPLOAD SPECS FOR PRODUCT DEMO:
 * @field videoUrl: Must be a standard embed URL (e.g., https://www.youtube.com/embed/<ID>).
 * The custom YouTubeVideoCard handles fetching the high-res thumbnail automatically.
 */
data class ProductDemoSectionData(
    val sectionId: String? = "product-demonstration-section",
    val sectionHeader: String = "MyHand<sup>®</sup> SYSTEM IN ACTION",
    val videoUrl: String = Res.Strings.Links.MYHAND_PRODUCT_DEMONSTRATION,
    val videoThumbnailUrl: String? = null, // <-- ADD THIS
    val videoLabelPillText: String? = "Watch · System Demonstration",
    val videoAriaLabel: String = "Play MyHand System Demonstration Video",
    val videoFormat: VideoFormat = VideoFormat.STANDARD_16_9
)

val defaultDemoData = ProductDemoSectionData()